import { CURRENCY, RESERVE, type TradeGoodId } from '@sd/content';
import { roundMoney } from './market';

/**
 * A player's currency (GDD §6). Cash is held in it; the exchange and every
 * price in content are in ГС. Pure functions only.
 */
export interface CurrencyState {
  /** ГС per unit of this currency. */
  readonly rate: number;
  /** Emission counted in the money supply (a reserve issuer exports part of it). */
  readonly emitted: number;
  /** Everything ever printed, for reports. */
  readonly printed: number;
  /**
   * Multiplier on the formula rate; below 1 after a currency crisis and
   * recovering every cycle (GDD §6: losing the reserve status).
   */
  readonly crisis: number;
  /** Value of each recent cycle's output at that cycle's market prices, ГС, oldest first. */
  readonly outputValue: readonly number[];
  /** Exchange turnover of each recent cycle, ГС, oldest first. */
  readonly turnover: readonly number[];
  /** Turnover of the cycle in progress outside the exchange (contracts), ГС. */
  readonly cycleTurnover: number;
  /**
   * The exchange this cycle, ГС per good, bought minus sold (#121, the author's
   * decision of 23.10.2026): only the net counts as turnover, so buying a good
   * and selling it back pumps nothing.
   */
  readonly cycleExchange?: Readonly<Partial<Record<TradeGoodId, number>>>;
}

/** What a cycle's trading adds to the turnover: contracts, and the exchange net per good. */
export function cycleTurnoverOf(currency: CurrencyState): number {
  const exchange = Object.values(currency.cycleExchange ?? {}).reduce<number>(
    (sum, v) => sum + Math.abs(v ?? 0),
    0,
  );
  return currency.cycleTurnover + exchange;
}

export function createCurrency(): CurrencyState {
  return {
    rate: CURRENCY.startRate,
    emitted: 0,
    printed: 0,
    crisis: 1,
    outputValue: [],
    turnover: [],
    cycleTurnover: 0,
  };
}

/** M: money held by the population plus everything printed. */
export function moneySupply(currency: CurrencyState, population: number): number {
  return CURRENCY.moneyPerPerson * population + currency.emitted;
}

const clampRate = (rate: number) =>
  Math.min(CURRENCY.maxRate, Math.max(CURRENCY.minRate, Number.isFinite(rate) ? rate : 0));

const windowSum = (values: readonly number[]) => values.reduce((a, b) => a + b, 0);

/** Output of the window, ГС; a young history is extrapolated to the full window. */
export function windowOutput(currency: CurrencyState): number {
  const n = currency.outputValue.length;
  return n === 0 ? 0 : (windowSum(currency.outputValue) / n) * CURRENCY.windowCycles;
}

/**
 * K = k0 · P / M · (1 + w · (s − 1/N)), clamped to the rate bounds.
 * `share` is this currency's share of all exchange turnover in the window.
 */
export function computeRate(
  currency: CurrencyState,
  population: number,
  share: number,
  players: number,
): number {
  if (currency.outputValue.length === 0) return clampRate(currency.rate);
  const supply = moneySupply(currency, population);
  const tradeFactor = 1 + CURRENCY.tradeShareWeight * (share - 1 / players);
  return clampRate(
    (CURRENCY.rateScale * windowOutput(currency) * tradeFactor * currency.crisis) / supply,
  );
}

/** Shares of exchange turnover over the window; equal shares when nobody traded. */
export function tradeShares(currencies: readonly CurrencyState[]): number[] {
  const totals = currencies.map((c) => windowSum(c.turnover));
  const all = windowSum(totals);
  return all > 0 ? totals.map((t) => t / all) : currencies.map(() => 1 / currencies.length);
}

/** Closes a cycle: records output and turnover, keeping the window; a crisis eases. */
export function recordCycle(currency: CurrencyState, outputValue: number): CurrencyState {
  return {
    ...currency,
    crisis: Math.min(1, currency.crisis + RESERVE.crisisDrop / RESERVE.crisisRecoveryCycles),
    outputValue: [...currency.outputValue, outputValue].slice(-CURRENCY.windowCycles),
    turnover: [...currency.turnover, cycleTurnoverOf(currency)].slice(-CURRENCY.windowCycles),
    cycleTurnover: 0,
    cycleExchange: {},
  };
}

/**
 * Prints `amount`. The rate falls at once in proportion to the money supply
 * (K · M / (M + amount)), so fresh money can never be spent at the old rate.
 * `inflationWeight` < 1 for a reserve issuer, who exports part of the inflation.
 */
export function emit(
  currency: CurrencyState,
  population: number,
  amount: number,
  inflationWeight = 1,
): CurrencyState {
  const before = moneySupply(currency, population);
  const counted = amount * inflationWeight;
  return {
    ...currency,
    emitted: currency.emitted + counted,
    printed: currency.printed + amount,
    rate: clampRate((currency.rate * before) / (before + counted)),
  };
}

/** Currency crisis: the rate drops now and recovers over the next cycles. */
export function startCrisis(currency: CurrencyState): CurrencyState {
  const crisis = 1 - RESERVE.crisisDrop;
  return { ...currency, crisis, rate: clampRate(currency.rate * crisis) };
}

/** ГС → the player's currency, without conversion (internal prices). */
export const toLocal = (gs: number, rate: number): number => roundMoney(gs / rate);
